'use client';

import { Button } from '@workspace/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@workspace/ui/components/command';
// import { useTranslations } from "next-intl";
import { ListItem } from '@workspace/ui/components/custom/listing';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { cn } from '@workspace/ui/lib';
import { ChevronDown, type LucideIcon, X } from 'lucide-react';
import Image from 'next/image';
import * as React from 'react';
import { type ReactNode, useMemo } from 'react';
import { Checkbox } from '../checkbox';

export interface ComboboxOption {
  value: string;
  name: string;
  icon?: LucideIcon;
  description?: string;
  image?: {
    path: string;
    alt: string;
  };
}

interface BaseComboboxProps {
  options: ComboboxOption[];
  placeholder?: ReactNode;
  emptyMessage?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  showFilter?: boolean;
  clearAllButton?: boolean;
  showListValues?: boolean;
  fuzzySearch?: boolean;
  'aria-invalid'?: boolean;
  readOnly?: boolean;
  onBlur?: () => void;
  inModal?: boolean;
  size?: 'base' | 'sm';
  minWidth?: string;
  maxWidth?: string;
  selectionName?: string;
  truncate?: boolean;
  /** When true, shows an X on the trigger to clear (single: value; multiple: all selections). */
  showClearButton?: boolean;
  /** Masks the selected value in PostHog session recordings for sensitive data. */
  sensitive?: boolean;
}

interface SingleComboboxProps extends BaseComboboxProps {
  multiple?: false;
  value: string;
  onChange: (value: string, option?: ComboboxOption) => void;
}

interface MultipleComboboxProps extends BaseComboboxProps {
  multiple: true;
  value: string[];
  onChange: (value: string[], options?: ComboboxOption[]) => void;
}

type ComboboxProps = SingleComboboxProps | MultipleComboboxProps;

export function Combobox({
  options,
  value,
  onChange,
  placeholder,
  emptyMessage,
  searchPlaceholder,
  className,
  disabled = false,
  multiple = false,
  showFilter = false,
  clearAllButton = false,
  showListValues = true,
  fuzzySearch = false,
  readOnly = false,
  onBlur,
  inModal = false,
  size = 'base',
  minWidth,
  maxWidth,
  selectionName,
  truncate = false,
  showClearButton = false,
  sensitive = false,
  ...props
}: ComboboxProps) {
  // const t = useTranslations("combobox");
  // const tForm = useTranslations("form");

  const placeholderText = typeof placeholder === 'string' ? (placeholder as string) : 'Select';
  const tPlaceholder = placeholder ?? placeholderText;

  // const tAllSelected = t("allSelected");
  // const tClearAll = t("clearAll");
  // const tSelected = t("selected");

  const [open, setOpen] = React.useState(false);
  const [hasScrollbar, setHasScrollbar] = React.useState(false);
  const commandListRef = React.useRef<HTMLDivElement>(null);

  const optionsMap = React.useMemo(() => {
    return options.reduce(
      (acc, option) => {
        acc[option.value] = option;
        return acc;
      },
      {} as Record<string, ComboboxOption>
    );
  }, [options]);

  // Check if scrollbar is present
  React.useEffect(() => {
    const checkScrollbar = () => {
      if (commandListRef.current) {
        const hasScroll = commandListRef.current.scrollHeight > commandListRef.current.clientHeight;
        setHasScrollbar(hasScroll);
      }
    };

    if (open) {
      // Use requestAnimationFrame for immediate visual update
      requestAnimationFrame(() => {
        checkScrollbar();
      });
    }
  }, [open]);

  const literalFilter = React.useCallback((value: string, search: string) => {
    return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
  }, []);

  const handleSearchChange = React.useCallback(() => {
    // Scroll to top when search input changes
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0;
    }
  }, []);

  const handleSelect = (currentValue: string) => {
    if (multiple) {
      const currentValues = value as string[];
      const newValues = currentValues.includes(currentValue)
        ? currentValues.filter((v) => v !== currentValue)
        : [...currentValues, currentValue];
      const selectedOptions = newValues.map((v) => optionsMap[v]).filter(Boolean);
      (onChange as (value: string[], options: ComboboxOption[]) => void)(
        newValues,
        selectedOptions as ComboboxOption[]
      );
    } else {
      const option = optionsMap[currentValue];
      (onChange as (value: string, option?: ComboboxOption) => void)(
        currentValue === value ? '' : currentValue,
        currentValue === value ? undefined : option
      );
      setOpen(false);
    }
  };

  const selectedValues = useMemo(() => (multiple ? (value as string[]) : []), [multiple, value]);
  const selectedValue = multiple ? '' : (value as string);

  const displayValue = multiple
    ? selectedValues.length > 0
      ? selectedValues.length === options.length
        ? 'All selected'
        : selectedValues.length === 1
          ? optionsMap[selectedValues[0] as string]?.name
          : selectionName
            ? `${selectionName} (${selectedValues.length})`
            : `${selectedValues.length} selected`
      : tPlaceholder
    : optionsMap[selectedValue]?.name || tPlaceholder;

  const renderVisualElement = (option: ComboboxOption) => {
    if (option.icon) {
      return (
        <div className="-ml-2 flex size-12 items-center justify-center rounded-2xl bg-pink-400 dark:bg-pink-400">
          <option.icon className="size-6 text-neutral-100 dark:text-neutral-100" />
        </div>
      );
    }
    if (option.image) {
      return <Image src={option.image.path} alt={option.image.alt} width={24} height={24} />;
    }
    return null;
  };

  const isPlaceholder = useMemo(
    () => (multiple ? selectedValues.length === 0 : !selectedValue),
    [multiple, selectedValues, selectedValue]
  );

  const popoverStyle = React.useMemo(() => {
    const style: React.CSSProperties & {
      '--combobox-popover-min-width'?: string;
      '--combobox-popover-max-width'?: string;
    } = {};
    if (minWidth) style['--combobox-popover-min-width'] = minWidth;
    if (maxWidth) style['--combobox-popover-max-width'] = maxWidth;
    return Object.keys(style).length > 0 ? style : undefined;
  }, [minWidth, maxWidth]);

  const showTriggerClearIcon =
    showClearButton && (multiple ? selectedValues.length > 0 : Boolean(selectedValue));

  return (
    <div className="flex w-full max-w-full min-w-0 flex-col gap-1">
      <Popover
        open={readOnly ? false : open}
        onOpenChange={(isOpen: boolean) => {
          setOpen(isOpen);
          if (!isOpen && onBlur) {
            onBlur();
          }
        }}
        modal={inModal}
      >
        <PopoverTrigger asChild>
          <Button
            variant="input"
            size="input"
            role="combobox"
            aria-expanded={open}
            aria-controls="combobox-list"
            aria-label={`Combobox ${isPlaceholder ? placeholderText : !multiple ? (optionsMap[selectedValue]?.name ?? '') : `${selectedValues.length} selected`}`}
            disabled={disabled}
            aria-invalid={props['aria-invalid'] || false}
            data-readonly={readOnly ? 'true' : undefined}
            className={cn(
              className,
              'w-full max-w-full min-w-0 shrink',
              readOnly &&
                'text-grey-600 dark:text-grey-600 border-grey-300! dark:border-grey-700! pointer-events-none cursor-default',
              size === 'sm' && 'h-9 rounded-xl px-3 py-2.5 text-sm',
              displayValue === tPlaceholder && !disabled && 'text-grey-700 dark:text-grey-500',
              'disabled:text-grey-300 dark:disabled:text-grey-900 disabled:border-grey-100'
            )}
          >
            <div
              className={cn(
                'flex min-w-0 flex-1 items-center gap-2 truncate',
                sensitive && 'ph-no-capture'
              )}
            >
              {!multiple && selectedValue && optionsMap[selectedValue]?.image && (
                <Image
                  src={optionsMap[selectedValue]?.image?.path ?? ''}
                  alt={optionsMap[selectedValue]?.image?.alt ?? ''}
                  width={24}
                  height={24}
                />
              )}
              {displayValue}
            </div>

            {!readOnly &&
              (showTriggerClearIcon ? (
                <div
                  className="flex h-5 w-5 shrink-0 items-center justify-center"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (multiple) {
                      (onChange as (value: string[], options: ComboboxOption[]) => void)([], []);
                    } else {
                      (onChange as (value: string, option?: ComboboxOption) => void)('', undefined);
                    }
                  }}
                >
                  <X className="group-disabled:text-grey-300 dark:group-disabled:text-grey-900 h-5 w-5 text-xl text-neutral-200 hover:text-red-500 dark:text-neutral-100 dark:hover:text-red-400" />
                </div>
              ) : (
                <ChevronDown className="group-disabled:text-grey-300 dark:group-disabled:text-grey-900 mt-1 h-5 w-5 stroke-[1.3px]! text-xl text-neutral-200 group-data-[state=open]:rotate-180 dark:text-neutral-100" />
              ))}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          onWheel={(e) => e.stopPropagation()}
          className={cn(
            'p-4 z-1000',
            'w-(--radix-popover-trigger-width) max-w-(--radix-popover-trigger-width) min-w-(--radix-popover-trigger-width)',
            minWidth && 'md:min-w-(--combobox-popover-min-width)',
            maxWidth && 'md:max-w-(--combobox-popover-max-width)'
          )}
          style={popoverStyle}
        >
          <Command {...(!fuzzySearch && { filter: literalFilter })}>
            {showFilter && options.length >= 10 && (
              <CommandInput
                placeholder={
                  !searchPlaceholder || searchPlaceholder === '' ? 'Search' : searchPlaceholder
                }
                onValueChange={handleSearchChange}
              />
            )}
            <CommandList
              ref={commandListRef}
              className={cn(
                'max-h-[275px] overflow-y-scroll overscroll-y-contain',
                !hasScrollbar && 'w-[calc(100%+8px)]'
              )}
            >
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup className="flex flex-col px-0 py-4">
                {options.map((option, index) => (
                  <CommandItem
                    key={option.value}
                    value={`${option.value}-${option.name}`}
                    onSelect={() => handleSelect(option.value)}
                    className={cn(
                      'flex h-12 cursor-pointer justify-between gap-2 rounded-2xl pr-3',
                      'hover:bg-grey-25 hover:dark:bg-grey-900 dark:text-grey-400 hover:text-neutral-200',
                      index !== options.length - 1 && 'mb-5',
                      (selectedValues.includes(option.value) || selectedValue === option.value) &&
                        'bg-grey-25 dark:bg-grey-900 dark:text-grey-400 font-medium text-neutral-200',
                      hasScrollbar && 'mr-2'
                    )}
                  >
                    <div
                      className={cn(
                        'flex items-center gap-3',
                        truncate && 'min-w-0 flex-1 overflow-hidden'
                      )}
                    >
                      {renderVisualElement(option)}
                      <span
                        className={cn(truncate && 'truncate block')}
                        title={truncate ? option.name : undefined}
                      >
                        {option.name}
                      </span>
                    </div>
                    {multiple && (
                      <Checkbox
                        id={option.value}
                        value={selectedValues.includes(option.value) ? 'true' : 'false'}
                        onChange={(e: React.FormEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelect(option.value);
                        }}
                      />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            {clearAllButton && (
              <Button
                variant="destructive"
                className="w-full"
                disabled={multiple ? selectedValues.length === 0 : !selectedValue}
                onClick={() => {
                  if (multiple) {
                    (onChange as (value: string[], options: ComboboxOption[]) => void)([], []);
                  } else {
                    (onChange as (value: string, option?: ComboboxOption) => void)('', undefined);
                  }
                }}
              >
                {'Clear all'}
              </Button>
            )}
          </Command>
        </PopoverContent>
      </Popover>
      {multiple && selectedValues.length > 0 && showListValues && (
        <div
          className="flex h-fit max-h-[228px] flex-col gap-0 overflow-y-scroll pr-2"
          data-slot="combobox-list"
        >
          {selectedValues.map((value) => {
            const option = optionsMap[value];
            return (
              <ListItem
                key={value}
                hasPointerEvents={false}
                title={option?.name || ''}
                id={value}
                icon={option ? renderVisualElement(option) : undefined}
                actionItems={
                  <Button variant="ghost" onClick={() => handleSelect(value)}>
                    <X />
                  </Button>
                }
                onChange={() => handleSelect(value)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
